import { Multistep, useFormApi, useMultistepApi } from 'informed';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { InputDate } from '@/components/form/Date';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { ScFormRegistration } from '@/components/form/groups/styled';
import { Input } from '@/components/form/Input';
import { InputPhoneNumber } from '@/components/form/InputPhoneNumber';
import { NamesSteps } from '@/helpers/auth';
import { notEmpty } from '@/helpers/common';
import { validateDate, validateName } from '@/helpers/validators';
import { sendAdditionalUserDataStep2NextEvent } from '@/modules/googleAnalytics/events/additionalDataEvents';
import { useRegModalTheme } from '@/modules/registration/hooks/useRegModalTheme';
import { useIsSports } from '@/modules/sports/hooks';
import { useGetCommonStore } from '@/store';
import { ScRegistrationWrapButtons } from '../../../styled';
import type { StepProps } from './type';

export const PersonalInformationStep: FC<StepProps> = ({ step, errors }) => {
  const { setCurrent, next } = useMultistepApi();
  const { getInitialValue } = useFormApi();
  const { styleScheme, input, select, buttons } = useRegModalTheme();

  const { t } = useTranslation(['common', 'validation']);
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();
  const isSports = useIsSports();

  const defaultCountryCode = useMemo(() => {
    if (
      getInitialValue(`${NamesSteps.PERSONAL_INFORMATION}.mobilePhone.code`) !=
        null ||
      countryCode == null
    )
      return;

    return countryCode;
  }, [getInitialValue, countryCode]);

  const [mobilePhoneError, setMobilePhoneError] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (
      errors.find(
        ({ field }) =>
          field === `${NamesSteps.PERSONAL_INFORMATION}.mobilePhone`
      )
    ) {
      setMobilePhoneError(t('validation:mobilePhone.invalid'));
      setCurrent(NamesSteps.PERSONAL_INFORMATION);
    }
  }, [errors, setCurrent, t]);

  return (
    <Multistep.Step step={step}>
      <ScFormRegistration styleScheme={styleScheme}>
        <FormGroup
          styleScheme={styleScheme}
          title={
            input.withInsideLabel ? '' : t('auth.registration.form.firstName')
          }
        >
          <Input
            disabled={!!user?.firstName}
            initialValue={user?.firstName}
            internalLabel={
              input.withInsideLabel ? t('auth.registration.form.firstName') : ''
            }
            name="firstName"
            placeholder={
              input.withInsideLabel ? '' : t('auth.registration.form.firstName')
            }
            styleScheme={styleScheme}
            testId="personal-info-first-name"
            validate={user?.firstName ? undefined : validateName}
            validateOn="blur"
            withInsideLabel={input.withInsideLabel}
          />
        </FormGroup>
        <FormGroup
          styleScheme={styleScheme}
          title={
            input.withInsideLabel ? '' : t('auth.registration.form.lastName')
          }
        >
          <Input
            disabled={!!user?.lastName}
            initialValue={user?.lastName}
            internalLabel={
              input.withInsideLabel ? t('auth.registration.form.lastName') : ''
            }
            name="lastName"
            placeholder={
              input.withInsideLabel ? '' : t('auth.registration.form.lastName')
            }
            styleScheme={styleScheme}
            testId="personal-info-last-name"
            validate={user?.lastName ? undefined : validateName}
            validateOn="blur"
            withInsideLabel={input.withInsideLabel}
          />
        </FormGroup>
        <FormGroup
          styleScheme={styleScheme}
          title={t('auth.registration.form.dateOfBirth')}
        >
          <InputDate
            disabled={notEmpty(user?.dateOfBirth)}
            initialValue={user?.dateOfBirth || undefined}
            name="dateOfBirth"
            testId="personal-info-date-of-birth"
            validate={validateDate(countryCode)}
          />
        </FormGroup>
        <FormGroup
          styleScheme={styleScheme}
          title={
            select?.withoutLabel ? '' : t('auth.registration.form.phoneNumber')
          }
        >
          <InputPhoneNumber
            disabled={notEmpty(user?.mobilePhone)}
            error={mobilePhoneError}
            initialCodeValue={defaultCountryCode}
            initialValue={user?.mobilePhone || undefined}
            name="mobilePhone"
            numberPlaceholder={
              isSports ? t('auth.registration.form.phoneNumber2') : ''
            }
            onBlur={() => setMobilePhoneError(undefined)}
            testId="personal-info-mobile-phone"
            validateOnBlur
            withValidate
          />
        </FormGroup>
      </ScFormRegistration>
      <ScRegistrationWrapButtons
        cols={1}
        marginBottom={buttons?.next.marginBottom}
      >
        <Button
          {...buttons?.next.colorMode}
          large
          onClick={() => {
            sendAdditionalUserDataStep2NextEvent();
            next();
          }}
          testId="personal-info-next-button"
          wide
        >
          {t('auth.registration.buttons.next')}
        </Button>
      </ScRegistrationWrapButtons>
    </Multistep.Step>
  );
};
